import React from 'react';

import type { Theme } from '../../../../lib/domain-types';
import { DESIGN_GRADIENTS } from '../../design.tokens';
import { NewBadge } from '../NewBadge';
import {
  LogoTile,
  LogoImg,
  Overlay,
  Name,
  NameRow,
  Subtitle,
  CompactSubtitle,
  Featured,
  FeaturedRow,
  Meta,
  Compact,
  CompactLogoWrap,
  CompactMeta,
} from './ThemeCard.styles';

/** Square logo tile for a theme. */
const Logo = ({ theme, size }: { theme: Theme; size: number }): React.ReactElement => (
  <LogoTile $size={size}>{theme.logo ? <LogoImg src={theme.logo} alt="" /> : null}</LogoTile>
);

export type ThemeCardProps = {
  theme: Theme;
  /** Render in the large featured layout (top slot). */
  featured: boolean;
  /** Whether this card holds the current selection frame. */
  selected: boolean;
  onSelect?: () => void;
};

/**
 * A theme option — large featured hero (top slot) or compact row card. The
 * "Recommended" badge from Figma renders at opacity 0 there, so it is omitted.
 */
export const ThemeCard = ({ theme, featured, selected, onSelect }: ThemeCardProps): React.ReactElement => {
  if (featured) {
    return (
      <Featured type="button" onClick={onSelect} data-ga-id={`cfg-design-theme-${theme.id}`} $selected={selected}>
        <Overlay $gradient={DESIGN_GRADIENTS.featuredOverlay} />
        <FeaturedRow>
          <Logo theme={theme} size={84} />
          <Meta>
            <NameRow>
              <Name text={theme.name} tag="paragraph" />
              {theme.isNew ? <NewBadge /> : null}
            </NameRow>
            <Subtitle text={theme.subtitle} tag="paragraph" />
          </Meta>
        </FeaturedRow>
      </Featured>
    );
  }

  return (
    <Compact type="button" onClick={onSelect} data-ga-id={`cfg-design-theme-${theme.id}`} $selected={selected}>
      <Overlay $gradient={DESIGN_GRADIENTS.compactOverlay} />
      <CompactLogoWrap>
        <Logo theme={theme} size={64} />
      </CompactLogoWrap>
      <CompactMeta>
        <NameRow>
          <Name text={theme.name} tag="paragraph" />
          {theme.isNew ? <NewBadge /> : null}
        </NameRow>
        <CompactSubtitle text={theme.subtitle} tag="paragraph" />
      </CompactMeta>
    </Compact>
  );
};

export default ThemeCard;
